import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { mapPatientListScopeTabToScope, useScopedPatientsQuery } from '@/entities/care-patients';
import { signOutSession } from '@/entities/session';
import { ClinicianProfileCard } from '@/features/patient-navigator/ui/ClinicianProfileCard';
import { PatientNavigator } from '@/features/patient-navigator';
import type { DateSortOrder, ScopedPatientTab } from '@/features/patient-navigator/model';
import { ANALYTICS_EVENTS, analytics, useDebouncedValue } from '@/shared';
import { closeLeftSidebar } from '@/shared/stores/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { CompactSearchField } from '@/shared/ui/primitives';
import { Home } from 'lucide-react';

import { OPEN_FOLLOW_UPS_ON_ARRIVAL } from './patientWorkspaceNavigation';

interface PatientNavigatorContentProps {
  activeUserId?: string;
  showFooter?: boolean;
}

function getNextDateSortOrder(current: DateSortOrder): DateSortOrder {
  switch (current) {
    case 'asc':
      return 'desc';
    case 'desc':
      return 'asc';
    default: {
      const exhaustiveCheck: never = current;
      return exhaustiveCheck;
    }
  }
}

function getDateSortLabel(order: DateSortOrder): string {
  switch (order) {
    case 'asc':
      return 'Oldest first';
    case 'desc':
      return 'Newest first';
    default: {
      const exhaustiveCheck: never = order;
      return exhaustiveCheck;
    }
  }
}

export function PatientNavigatorContent({
  activeUserId,
  showFooter = false,
}: PatientNavigatorContentProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const [dateSortOrder, setDateSortOrder] = useState<DateSortOrder>('asc');
  const [activeTab, setActiveTab] = useState<ScopedPatientTab>('urgent');
  const debouncedQuery = useDebouncedValue(query, 250);
  const trimmedQuery = debouncedQuery.trim();
  const scope = mapPatientListScopeTabToScope(activeTab);

  const { patients, isLoading } = useScopedPatientsQuery({ query: trimmedQuery, scope });

  const handleActiveTabChange = useCallback((tab: ScopedPatientTab) => {
    setActiveTab(tab);
  }, []);

  const handleSelectPatient = (userId: string) => {
    const patient = patients.find((p) => p.userId === userId);
    void analytics.track({
      name: ANALYTICS_EVENTS.navigator.patientSelected,
      context: {
        actor: 'clinician',
        patientUserId: userId,
      },
      properties: {
        selectedPatientUserId: userId,
        unreadCount: patient?.unreadCount,
        urgentCount: patient?.urgentCount,
        routineCount: patient?.routineCount,
      },
    });

    const hasUnread = patient !== undefined && patient.unreadCount > 0;
    navigate(`/patient/${userId}`, {
      state: hasUnread ? OPEN_FOLLOW_UPS_ON_ARRIVAL : undefined,
    });
    closeLeftSidebar();
  };

  return (
    <PatientNavigator
      patients={patients}
      activeTab={activeTab}
      isPatientsLoading={isLoading}
      activeUserId={activeUserId}
      onSelectPatient={handleSelectPatient}
      onActiveTabChange={handleActiveTabChange}
      dateSortOrder={dateSortOrder}
      header={
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <CompactSearchField
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Search patients"
              className="flex-1 rounded-full"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-full border border-border/70 px-2.5 text-xs text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={() => setDateSortOrder(getNextDateSortOrder(dateSortOrder))}
              aria-label={`Sort by date: ${getDateSortLabel(dateSortOrder)}. Click to toggle sorting direction.`}
            >
              {getDateSortLabel(dateSortOrder)}
            </Button>
          </div>
          <div className="px-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-fit rounded-full border border-border/60 px-2 text-xs text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={() => navigate('/dashboard')}
              aria-label="Home"
            >
              <Home className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              Home
            </Button>
          </div>
        </div>
      }
      footer={
        showFooter ? (
          <div className="px-1">
            <ClinicianProfileCard onSignOut={() => void signOutSession()} />
          </div>
        ) : undefined
      }
    />
  );
}
